import { useState, useEffect, useCallback } from 'react';
import API from '../api/axios';

// ─── tiny reusable helpers ────────────────────────────────────────────────────

const Badge = ({ ok, label }) => (
  <span style={{
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    background: ok === null ? '#e2e8f0' : ok ? '#d1fae5' : '#fee2e2',
    color:      ok === null ? '#64748b'  : ok ? '#065f46' : '#991b1b',
  }}>
    {ok === null ? 'Not tested' : ok ? '✓ ' + label : '✗ ' + label}
  </span>
);

const SectionCard = ({ title, children }) => (
  <div style={{
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '20px 24px',
    marginBottom: 20,
  }}>
    <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{title}</h3>
    {children}
  </div>
);

const InputRow = ({ label, hint, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
      {label}
    </label>
    {children}
    {hint && <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280' }}>{hint}</p>}
  </div>
);

// ─── Model list editor (add / remove / reorder) ───────────────────────────────

const ModelList = ({ models, onChange, placeholder = 'model-name:tag' }) => {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (!v || models.includes(v)) return;
    onChange([...models, v]);
    setDraft('');
  };

  const remove = (idx) => onChange(models.filter((_, i) => i !== idx));

  const move = (idx, dir) => {
    const next = [...models];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange(next);
  };

  return (
    <div>
      {models.length === 0 && (
        <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 8px' }}>No models configured — add one below.</p>
      )}
      {models.map((m, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#f8fafc', border: '1px solid #e2e8f0',
          borderRadius: 6, padding: '6px 10px', marginBottom: 6,
        }}>
          <span style={{
            flex: 1, fontFamily: 'monospace', fontSize: 13, color: '#1e293b',
          }}>{m}</span>
          <span style={{ fontSize: 11, color: '#64748b', background: '#e2e8f0', borderRadius: 4, padding: '1px 6px' }}>
            Priority {i + 1}
          </span>
          <button onClick={() => move(i, -1)} disabled={i === 0}
            style={btnSm} title="Move up">↑</button>
          <button onClick={() => move(i, 1)} disabled={i === models.length - 1}
            style={btnSm} title="Move down">↓</button>
          <button onClick={() => remove(i)}
            style={{ ...btnSm, color: '#dc2626', borderColor: '#fca5a5' }} title="Remove">×</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder={placeholder}
          style={inputStyle}
        />
        <button onClick={add} style={btnPrimary}>Add</button>
      </div>
    </div>
  );
};

// ─── Provider priority reorder ────────────────────────────────────────────────

const PROVIDER_LABELS = { ollama: 'Ollama (local)', gemini: 'Gemini (cloud)' };

const ProviderPriorityEditor = ({ order, onChange }) => {
  const move = (idx, dir) => {
    const next = [...order];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange(next);
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 10px' }}>
        The first provider is used for all features. If all its models fail, the system automatically falls back to the next provider.
      </p>
      {order.map((p, i) => (
        <div key={p} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', marginBottom: 8,
          borderRadius: 8,
          background: i === 0 ? '#eff6ff' : '#f8fafc',
          border: `1px solid ${i === 0 ? '#bfdbfe' : '#e2e8f0'}`,
        }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: i === 0 ? '#1d4ed8' : '#64748b',
            background: i === 0 ? '#dbeafe' : '#e2e8f0',
            borderRadius: 10, padding: '2px 8px', minWidth: 58, textAlign: 'center',
          }}>
            {i === 0 ? 'PRIMARY' : 'FALLBACK'}
          </span>
          <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
            {PROVIDER_LABELS[p] || p}
          </span>
          <button onClick={() => move(i, -1)} disabled={i === 0} style={btnSm} title="Move up">↑</button>
          <button onClick={() => move(i, 1)} disabled={i === order.length - 1} style={btnSm} title="Move down">↓</button>
        </div>
      ))}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const inputStyle = {
  flex: 1, padding: '7px 10px', fontSize: 13,
  border: '1px solid #d1d5db', borderRadius: 6,
  outline: 'none', fontFamily: 'inherit',
  background: '#fff',
};

const btnPrimary = {
  padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
  whiteSpace: 'nowrap',
};

const btnSm = {
  padding: '3px 8px', fontSize: 13, cursor: 'pointer',
  background: '#fff', border: '1px solid #d1d5db', borderRadius: 4,
};

const btnOutline = {
  padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6,
};

// ─── Main component ───────────────────────────────────────────────────────────

const SuperAdminAISettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingOllama, setTestingOllama] = useState(false);
  const [testingGemini, setTestingGemini] = useState(false);
  const [toast, setToast] = useState(null);

  // Connection test results
  const [ollamaStatus, setOllamaStatus] = useState({ ok: null, message: '', models: [] });
  const [geminiStatus, setGeminiStatus] = useState({ ok: null, message: '', models: [] });

  // Settings state
  const [settings, setSettings] = useState({
    providerPriority: ['ollama', 'gemini'],
    ollamaBaseUrl: 'http://27.107.225.61:11434',
    ollamaModels: ['edulab/edulab1:latest'],
    ollamaExtractionModel: 'qwen2.5vl:7b',
    geminiModels: [],
    geminiVisionModels: [],
    batchSizeOllama: 10,
    cooldownMs: 300000,
    timeoutMs: 120000,
  });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const patch = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/superadmin/ai-settings');
      if (data.success) setSettings(data.settings);
    } catch (err) {
      showToast('Failed to load settings: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put('/superadmin/ai-settings', settings);
      if (data.success) showToast('Settings saved successfully');
      else showToast(data.message || 'Save failed', 'error');
    } catch (err) {
      showToast('Save failed: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const testOllama = async () => {
    setTestingOllama(true);
    try {
      const { data } = await API.post('/superadmin/ai-settings/test-ollama', {
        baseUrl: settings.ollamaBaseUrl,
      });
      setOllamaStatus({ ok: data.healthy, message: data.message, models: data.models || [] });
    } catch (err) {
      setOllamaStatus({ ok: false, message: err.response?.data?.message || err.message, models: [] });
    } finally {
      setTestingOllama(false);
    }
  };

  const testGemini = async () => {
    setTestingGemini(true);
    try {
      const { data } = await API.post('/superadmin/ai-settings/test-gemini', {});
      setGeminiStatus({ ok: data.success && data.configured, message: data.message, models: data.models || [] });
    } catch (err) {
      setGeminiStatus({ ok: false, message: err.response?.data?.message || err.message, models: [] });
    } finally {
      setTestingGemini(false);
    }
  };

  // Convert ms ↔ human-friendly units for display
  const cooldownMin = Math.round(settings.cooldownMs / 60000);
  const timeoutSec  = Math.round(settings.timeoutMs / 1000);

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>
        Loading AI settings…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: '24px 16px' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
          background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {toast.msg}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            AI Provider Settings
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
            Configure which AI provider drives generation, voice commands, and template extraction system-wide.
          </p>
        </div>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, padding: '9px 22px', fontSize: 14 }}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>

      {/* ── Provider Priority ── */}
      <SectionCard title="Provider Priority">
        <ProviderPriorityEditor
          order={settings.providerPriority}
          onChange={v => patch('providerPriority', v)}
        />
        <p style={{ margin: '12px 0 0', fontSize: 12, color: '#9ca3af' }}>
          Note: Template extraction (PDF parsing) always uses Gemini because it requires native PDF vision — Ollama is used for generation and voice commands.
        </p>
      </SectionCard>

      {/* ── Ollama Configuration ── */}
      <SectionCard title="Ollama Configuration">
        <InputRow label="Server Base URL" hint="Override the OLLAMA_BASE_URL environment variable. Changes take effect on next request.">
          <input
            value={settings.ollamaBaseUrl}
            onChange={e => patch('ollamaBaseUrl', e.target.value)}
            style={inputStyle}
            placeholder="http://hostname:11434"
          />
        </InputRow>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center' }}>
          <button onClick={testOllama} disabled={testingOllama} style={btnOutline}>
            {testingOllama ? 'Testing…' : 'Test Connection'}
          </button>
          <Badge ok={ollamaStatus.ok} label={ollamaStatus.ok ? 'Online' : 'Offline'} />
          {ollamaStatus.message && (
            <span style={{ fontSize: 12, color: '#6b7280' }}>{ollamaStatus.message}</span>
          )}
        </div>
        {ollamaStatus.models.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', margin: '0 0 4px' }}>
              Models available on server:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ollamaStatus.models.map(m => (
                <span key={m} style={{
                  fontSize: 12, fontFamily: 'monospace',
                  background: '#f0f9ff', border: '1px solid #bae6fd',
                  borderRadius: 4, padding: '2px 8px', color: '#0369a1',
                }}>{m}</span>
              ))}
            </div>
          </div>
        )}

        <InputRow
          label="Generation Models"
          hint="Ordered list — model #1 is tried first. If it fails, model #2 is used, then Gemini fallback."
        >
          <ModelList
            models={settings.ollamaModels}
            onChange={v => patch('ollamaModels', v)}
            placeholder="e.g. edulab/edulab1:latest"
          />
        </InputRow>

        <InputRow
          label="Extraction / Vision Model"
          hint="Used for voice NLU when Ollama is primary. Template extraction uses Gemini."
        >
          <input
            value={settings.ollamaExtractionModel}
            onChange={e => patch('ollamaExtractionModel', e.target.value)}
            style={inputStyle}
            placeholder="e.g. qwen2.5vl:7b"
          />
        </InputRow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <InputRow label="Batch Size" hint="Max variations per Ollama call (default 10)">
            <input
              type="number" min={1} max={50}
              value={settings.batchSizeOllama}
              onChange={e => patch('batchSizeOllama', parseInt(e.target.value, 10) || 10)}
              style={inputStyle}
            />
          </InputRow>
          <InputRow label="Cooldown (minutes)" hint="How long a failed model is skipped">
            <input
              type="number" min={0} max={60}
              value={cooldownMin}
              onChange={e => patch('cooldownMs', (parseInt(e.target.value, 10) || 0) * 60000)}
              style={inputStyle}
            />
          </InputRow>
          <InputRow label="Request Timeout (seconds)" hint="Per-call HTTP timeout">
            <input
              type="number" min={5} max={600}
              value={timeoutSec}
              onChange={e => patch('timeoutMs', (parseInt(e.target.value, 10) || 120) * 1000)}
              style={inputStyle}
            />
          </InputRow>
        </div>
      </SectionCard>

      {/* ── Gemini Configuration ── */}
      <SectionCard title="Gemini Configuration">
        <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center' }}>
          <button onClick={testGemini} disabled={testingGemini} style={btnOutline}>
            {testingGemini ? 'Testing…' : 'Test API Key'}
          </button>
          <Badge ok={geminiStatus.ok} label={geminiStatus.ok ? 'Valid' : 'Invalid / Not Set'} />
          {geminiStatus.message && (
            <span style={{ fontSize: 12, color: '#6b7280' }}>{geminiStatus.message}</span>
          )}
        </div>
        {geminiStatus.models.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', margin: '0 0 4px' }}>
              Available Gemini models:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {geminiStatus.models.map(m => (
                <span key={m} style={{
                  fontSize: 12, fontFamily: 'monospace',
                  background: '#fdf4ff', border: '1px solid #e9d5ff',
                  borderRadius: 4, padding: '2px 8px', color: '#7e22ce',
                }}>{m}</span>
              ))}
            </div>
          </div>
        )}

        <InputRow
          label="Generation Models"
          hint="Ordered rotation for question/text generation — model #1 is tried first, falling back down the list. Latest Gemini 3 models are kept on top."
        >
          <ModelList
            models={settings.geminiModels}
            onChange={v => patch('geminiModels', v)}
            placeholder="e.g. gemini-3-pro-preview"
          />
        </InputRow>

        <InputRow
          label="Vision / Extraction Models"
          hint="Ordered rotation for PDF template extraction and image analysis — model #1 is tried first, falling back down the list."
        >
          <ModelList
            models={settings.geminiVisionModels}
            onChange={v => patch('geminiVisionModels', v)}
            placeholder="e.g. gemini-3-pro-preview"
          />
        </InputRow>

        <div style={{
          padding: '10px 14px', borderRadius: 6,
          background: '#fffbeb', border: '1px solid #fde68a',
          fontSize: 13, color: '#92400e',
        }}>
          The Gemini API key is configured via the <code style={{ fontFamily: 'monospace' }}>GEMINI_API_KEY</code> environment variable on the server and cannot be changed from this UI.
        </div>
      </SectionCard>

      {/* ── How the System Uses These Settings ── */}
      <SectionCard title="How the System Uses These Settings">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Feature', 'Uses Primary Provider?', 'Fallback'].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#374151' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['Question variation generation', 'Yes — batches of ' + settings.batchSizeOllama + ' (Ollama)', 'Gemini per batch'],
              ['Voice command NLU', 'Yes', 'Gemini'],
              ['Template extraction (PDF)', 'No — always Gemini', 'Gemini model rotation'],
              ['Gemini text generation (other)', 'If Gemini is primary', 'N/A'],
            ].map(([feat, uses, fb], i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '8px 12px', color: '#1e293b' }}>{feat}</td>
                <td style={{ padding: '8px 12px', color: '#374151' }}>{uses}</td>
                <td style={{ padding: '8px 12px', color: '#6b7280' }}>{fb}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>

      {/* Bottom save button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, padding: '10px 28px', fontSize: 14 }}>
          {saving ? 'Saving…' : 'Save All Settings'}
        </button>
      </div>
    </div>
  );
};

export default SuperAdminAISettings;
